import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, Save } from 'lucide-react';
import api from '../../services/api';
import { useDialogs } from '../../components/Dialogs';
import { field, ghost, primary, muted, card, rowBorder, errorText } from '../time/styles';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import { useTabParam } from '../../hooks/useTabParam';
import { Message } from '../time/ui';
import LettersTab from './LettersTab';
import ChecklistsTab from './ChecklistsTab';
import FormsTab from './FormsTab';

// Admin/HR → Lifecycle setup: the rules and approval flows that onboarding
// and exits follow. Everything here is per organisation.

const TYPE_LABELS = { FULL_TIME: 'Full-time', CONTRACTOR: 'Contractor', PARTNER: 'Partner', INTERN: 'Intern' };
const LEAVE_LABELS = { CASUAL: 'Casual', SICK: 'Sick', EARNED: 'Earned' };
const ROLE_LABELS = { ADMIN: 'Admin', HR: 'HR', FINANCE: 'Finance', MANAGER: 'Managers' };
// Readable rule names (also the accessible names of the Override ticks).
const RULE_TITLES = {
    noticeDays: 'Notice period', noticeDaysOnProbation: 'Notice during probation', probationMonths: 'Probation',
    buyoutAllowed: 'Notice buyout', settlementDueDays: 'Full & final deadline', inviteExpiryDays: 'Invite expiry',
    encashment: 'Leave encashment', shortfallRecovery: 'Unserved notice recovery', gratuity: 'Gratuity',
};
const scopeName = (p) => [p.department?.name, p.employmentType && TYPE_LABELS[p.employmentType]].filter(Boolean).join(' · ') || 'Organisation default';

// Inherited values with this scope's own on top; nested rules merge key by
// key (an override may set only encashment.maxDays).
const mergeRules = (base, over) => {
    const out = JSON.parse(JSON.stringify(base || {}));
    for (const [k, v] of Object.entries(over || {})) out[k] = v && typeof v === 'object' && !Array.isArray(v) ? { ...(out[k] || {}), ...v } : v;
    return out;
};
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const Num = ({ label, value, onChange, disabled, min = 0, max = 365, suffix }) => (
    <label style={{ display: 'grid', gap: 4 }}><span style={muted}>{label}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" required aria-label={label} min={min} max={max} style={{ ...field, width: 100 }} value={value ?? ''} disabled={disabled}
                onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
            {suffix && <span style={muted}>{suffix}</span>}
        </span>
    </label>
);

// The editable rule set. In "override" mode each top-level rule has an
// Override tick; unticked rules show (and keep) the inherited value.
const RulesForm = ({ initial, inherited, override, onSave, onCancel, saving }) => {
    const [rules, setRules] = useState(() => mergeRules(inherited, initial));
    const [on, setOn] = useState(() => new Set(override ? Object.keys(initial) : Object.keys(inherited)));
    const set = (k, v) => setRules(r => ({ ...r, [k]: v }));
    const setNested = (k, nk, v) => setRules(r => ({ ...r, [k]: { ...r[k], [nk]: v } }));
    const enabled = (k) => !override || on.has(k);
    const Toggle = ({ k }) => override ? (
        <label style={{ ...muted, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" aria-label={`Override: ${RULE_TITLES[k]}`} checked={on.has(k)} onChange={e => setOn(s => { const n = new Set(s); if (e.target.checked) n.add(k); else { n.delete(k); set(k, inherited[k]); } return n; })} /> Override
        </label>
    ) : null;
    const box = (k, children) => (
        <div style={{ padding: 12, border: rowBorder, borderRadius: 8, opacity: enabled(k) ? 1 : 0.55, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Toggle k={k} /></div>
            {children}
        </div>
    );
    const submit = (e) => {
        e.preventDefault();
        const keys = override ? [...on] : Object.keys(inherited);
        onSave(Object.fromEntries(keys.map(k => [k, rules[k]])));
    };
    const enc = rules.encashment || {};
    const rec = rules.shortfallRecovery || {};
    const gra = rules.gratuity || {};
    return (
        <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                {box('noticeDays', <Num label="Notice period" suffix="days" value={rules.noticeDays} disabled={!enabled('noticeDays')} onChange={v => set('noticeDays', v)} />)}
                {box('noticeDaysOnProbation', <Num label="Notice during probation" suffix="days" value={rules.noticeDaysOnProbation} disabled={!enabled('noticeDaysOnProbation')} onChange={v => set('noticeDaysOnProbation', v)} />)}
                {box('probationMonths', <Num label="Probation" suffix="months" max={24} value={rules.probationMonths} disabled={!enabled('probationMonths')} onChange={v => set('probationMonths', v)} />)}
                {box('buyoutAllowed', <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" aria-label="Notice buyout allowed" checked={!!rules.buyoutAllowed} disabled={!enabled('buyoutAllowed')} onChange={e => set('buyoutAllowed', e.target.checked)} /> Employees may buy out unserved notice</label>)}
                {box('settlementDueDays', <Num label="Full & final due within" suffix="days after last day" min={1} max={180} value={rules.settlementDueDays} disabled={!enabled('settlementDueDays')} onChange={v => set('settlementDueDays', v)} />)}
                {box('inviteExpiryDays', <Num label="Pre-joiner invite expires after" suffix="days" min={1} max={180} value={rules.inviteExpiryDays} disabled={!enabled('inviteExpiryDays')} onChange={v => set('inviteExpiryDays', v)} />)}
                {box('encashment', (
                    <div style={{ display: 'grid', gap: 8 }}>
                        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" aria-label="Leave encashment on exit" checked={!!enc.enabled} disabled={!enabled('encashment')} onChange={e => setNested('encashment', 'enabled', e.target.checked)} /> <strong>Pay for unused leave on exit</strong></label>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {Object.entries(LEAVE_LABELS).map(([k, l]) => (
                                <label key={k} style={muted}><input type="checkbox" disabled={!enabled('encashment') || !enc.enabled} checked={(enc.leaveTypes || []).includes(k)}
                                    onChange={e => setNested('encashment', 'leaveTypes', e.target.checked ? [...(enc.leaveTypes || []), k] : (enc.leaveTypes || []).filter(t => t !== k))} /> {l}</label>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <Num label="Up to" suffix="days" value={enc.maxDays} disabled={!enabled('encashment') || !enc.enabled} onChange={v => setNested('encashment', 'maxDays', v)} />
                            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Day rate from</span>
                                <select aria-label="Encashment basis" style={field} value={enc.basis} disabled={!enabled('encashment') || !enc.enabled} onChange={e => setNested('encashment', 'basis', e.target.value)}>
                                    <option value="BASIC">Basic pay</option><option value="GROSS">Gross pay</option>
                                </select></label>
                            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>÷ days</span>
                                <select aria-label="Encashment divisor" style={field} value={String(enc.divisor)} disabled={!enabled('encashment') || !enc.enabled} onChange={e => setNested('encashment', 'divisor', e.target.value === 'ACTUAL' ? 'ACTUAL' : Number(e.target.value))}>
                                    <option value="30">30</option><option value="26">26</option><option value="ACTUAL">days in month</option>
                                </select></label>
                        </div>
                    </div>
                ))}
                {box('shortfallRecovery', (
                    <div style={{ display: 'grid', gap: 8 }}>
                        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" aria-label="Recover unserved notice" checked={!!rec.enabled} disabled={!enabled('shortfallRecovery')} onChange={e => setNested('shortfallRecovery', 'enabled', e.target.checked)} /> <strong>Recover pay for unserved notice</strong></label>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Day rate from</span>
                                <select aria-label="Recovery basis" style={field} value={rec.basis} disabled={!enabled('shortfallRecovery') || !rec.enabled} onChange={e => setNested('shortfallRecovery', 'basis', e.target.value)}>
                                    <option value="BASIC">Basic pay</option><option value="GROSS">Gross pay</option>
                                </select></label>
                            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>÷ days</span>
                                <select aria-label="Recovery divisor" style={field} value={String(rec.divisor)} disabled={!enabled('shortfallRecovery') || !rec.enabled} onChange={e => setNested('shortfallRecovery', 'divisor', e.target.value === 'ACTUAL' ? 'ACTUAL' : Number(e.target.value))}>
                                    <option value="30">30</option><option value="26">26</option><option value="ACTUAL">days in month</option>
                                </select></label>
                        </div>
                    </div>
                ))}
                {box('gratuity', (
                    <div style={{ display: 'grid', gap: 8 }}>
                        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" aria-label="Work out gratuity on exit" checked={!!gra.enabled} disabled={!enabled('gratuity')} onChange={e => setNested('gratuity', 'enabled', e.target.checked)} /> Work out gratuity in the settlement (full-time employees)</label>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <Num label="Payable after" suffix="years" max={30} value={gra.minYears} disabled={!enabled('gratuity') || !gra.enabled} onChange={v => setNested('gratuity', 'minYears', v)} />
                            <Num label="Days of Basic per year" suffix="days" min={1} max={60} value={gra.daysPerYear} disabled={!enabled('gratuity') || !gra.enabled} onChange={v => setNested('gratuity', 'daysPerYear', v)} />
                            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>÷ days</span>
                                <select aria-label="Gratuity divisor" style={field} value={String(gra.divisor)} disabled={!enabled('gratuity') || !gra.enabled} onChange={e => setNested('gratuity', 'divisor', e.target.value === 'ACTUAL' ? 'ACTUAL' : Number(e.target.value))}>
                                    <option value="26">26</option><option value="30">30</option><option value="ACTUAL">days in month</option>
                                </select></label>
                            <Num label="Cap (0 for none)" max={1000000000} value={gra.maxAmount} disabled={!enabled('gratuity') || !gra.enabled} onChange={v => setNested('gratuity', 'maxAmount', v)} />
                        </div>
                        <span style={muted}>The years needed are completed years; a part year of more than six months then counts as a year in the amount. A retirement is paid whatever the service.</span>
                    </div>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {onCancel && <button type="button" style={ghost} onClick={onCancel}>Cancel</button>}
                <button type="submit" className="btn btn-primary" style={primary} disabled={saving}><Save size={14} /> Save rules</button>
            </div>
        </form>
    );
};

// One override (new or existing): loads what its scope inherits so the
// unticked rules show the values that will really apply.
const OverrideEditor = ({ scope, saving, onSave, onCancel }) => {
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let live = true;
        const q = new URLSearchParams({ ...(scope.departmentId ? { departmentId: scope.departmentId } : {}), ...(scope.employmentType ? { employmentType: scope.employmentType } : {}) });
        api.get(`/lifecycle/settings/policies/inherited?${q}`).then(r => { if (live) setInfo(r.data); }).catch(e => { if (live) setError(errorText(e, 'Failed to load')); });
        return () => { live = false; };
    }, [scope.departmentId, scope.employmentType]);
    if (error) return <p style={{ color: '#ef4444' }}>{error}</p>;
    if (!info) return <p style={muted}>Loading…</p>;
    return (
        <>
            {scope.isNew && info.existing && <p style={{ ...muted, color: '#d97706' }}>An override for this already exists, so you are editing it.</p>}
            <RulesForm key={info.scopeKey} override initial={info.existing?.rules || {}} inherited={info.inherited} saving={saving} onCancel={onCancel} onSave={onSave} />
        </>
    );
};

// Which earning rule is the Basic pay, for leave encashment and notice recovery.
const BasicPayCard = ({ say }) => {
    const [d, setD] = useState(null);
    const [saving, setSaving] = useState(false);
    const { confirm, dialogs } = useDialogs();
    const load = useCallback(() => api.get('/lifecycle/settings/basic').then(r => setD(r.data)).catch(e => say(false, errorText(e, 'Could not load the Basic pay setting'))), [say]);
    useEffect(() => { load(); }, [load]);
    if (!d) return null;
    const choose = async (ruleName) => {
        if (!await confirm({ title: 'Change the Basic pay rule?', message: 'It changes how leave encashment, notice recovery and gratuity are worked out in settlements prepared from now on. Settlements already prepared keep their figures until recalculated.', confirmText: 'Change' })) return;
        setSaving(true);
        try { await api.put('/lifecycle/settings/basic', { ruleName }); say(true, ruleName ? `Basic pay is now "${ruleName}"` : 'Basic pay is found by its name again'); await load(); } catch (e) { say(false, errorText(e, 'Save failed')); } finally { setSaving(false); }
    };
    const auto = d.detected ? `found automatically: "${d.detected}"` : 'none found yet';
    return (
        <section className="card" style={card} aria-label="Basic pay component">
            {dialogs}
            <h3 style={{ margin: '0 0 8px' }}>Basic pay component</h3>
            <p style={{ ...muted, marginTop: 0 }}>Leave encashment and notice recovery based on Basic pay use this earning rule. Left on automatic, a rule called Basic, Basic salary or Basic pay is used.</p>
            <label style={{ display: 'grid', gap: 4, maxWidth: 320 }}>
                <span style={muted}>Earning rule that is the Basic</span>
                <select aria-label="Basic pay earning rule" style={field} disabled={saving} value={d.chosen || ''} onChange={e => choose(e.target.value)}>
                    <option value="">Automatic ({auto})</option>
                    {d.rules.map(n => <option key={n} value={n}>{n}</option>)}
                    {d.chosen && !d.chosenExists && <option value={d.chosen}>{d.chosen} (no longer exists)</option>}
                </select>
            </label>
            {d.chosen && !d.chosenExists && <p role="alert" style={{ color: 'var(--danger, #b42318)', marginBottom: 0 }}>The chosen rule no longer exists, so settlements cannot find the Basic. Choose another.</p>}
        </section>
    );
};

const RulesTab = ({ departments, say }) => {
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(null); // 'default' | policy id | 'new'
    const [scope, setScope] = useState({ departmentId: '', employmentType: '' });
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => api.get('/lifecycle/settings/policies').then(r => setData(r.data)).catch(e => say(false, errorText(e, 'Failed to load rules'))), [say]);
    useEffect(() => { load(); }, [load]);
    if (!data) return <p style={muted}>Loading…</p>;

    const overrides = data.policies.filter(p => p.departmentId || p.employmentType);
    const save = async (body, msg) => {
        setSaving(true);
        try {
            const res = await api.put('/lifecycle/settings/policies', body);
            say(true, res.data.removed ? (body.departmentId || body.employmentType ? 'Override removed: nothing was overridden' : 'Default rules saved (all built-in values)') : msg);
            setEditing(null);
            await load();
        } catch (e) { say(false, errorText(e, 'Save failed')); }
        finally { setSaving(false); }
    };
    // The default only stores what differs from the built-in values, so
    // future improvements to the built-ins still reach this organisation.
    const saveDefault = (rules) => save({ rules: Object.fromEntries(Object.entries(rules).filter(([k, v]) => !same(v, data.builtIn[k]))) }, 'Default rules saved');
    const describe = (rules) => Object.entries(rules).map(([k, v]) => {
        if (k === 'encashment') return v.enabled === false ? 'no leave encashment' : `encash ${(v.leaveTypes || []).join('/').toLowerCase() || '(inherited types)'}${v.maxDays !== undefined ? ` up to ${v.maxDays} days` : ''}`;
        if (k === 'shortfallRecovery') return v.enabled === false ? 'no notice recovery' : 'notice recovery settings';
        if (k === 'gratuity') return v.enabled ? `gratuity after ${v.minYears} years` : 'no gratuity';
        if (k === 'buyoutAllowed') return v ? 'buyout allowed' : 'no buyout';
        return `${data.labels[k]}: ${v}`;
    }).join(' · ');

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <section className="card" style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <h3 style={{ margin: 0, flex: 1 }}>Organisation default</h3>
                    {editing !== 'default' && <button style={ghost} onClick={() => setEditing('default')}>Edit</button>}
                </div>
                <p style={{ ...muted, marginTop: 0 }}>Applies to everyone. Overrides below win where they are set: a department override beats this default, an employment-type override beats a department one, and an override for both beats either.</p>
                {editing === 'default'
                    ? <RulesForm initial={{}} inherited={data.effectiveDefault} saving={saving} onCancel={() => setEditing(null)} onSave={saveDefault} />
                    : <p style={{ margin: 0, color: 'var(--text-main)' }}>{describe(data.effectiveDefault)}</p>}
            </section>

            <BasicPayCard say={say} />

            <section className="card" style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <h3 style={{ margin: 0, flex: 1 }}>Overrides</h3>
                    {editing !== 'new' && <button style={ghost} onClick={() => { setScope({ departmentId: '', employmentType: '' }); setEditing('new'); }}><Plus size={13} /> Add override</button>}
                </div>
                {editing === 'new' && (
                    <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <select aria-label="Override department" style={field} value={scope.departmentId} onChange={e => setScope({ ...scope, departmentId: e.target.value })}>
                                <option value="">Any department</option>
                                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                            <select aria-label="Override employment type" style={field} value={scope.employmentType} onChange={e => setScope({ ...scope, employmentType: e.target.value })}>
                                <option value="">Any employment type</option>
                                {data.employmentTypes.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                            </select>
                        </div>
                        {(scope.departmentId || scope.employmentType)
                            ? <OverrideEditor scope={{ ...scope, isNew: true }} saving={saving} onCancel={() => setEditing(null)}
                                onSave={(rules) => save({ departmentId: scope.departmentId || null, employmentType: scope.employmentType || null, rules }, 'Override saved')} />
                            : <p style={muted}>Choose a department, an employment type, or both.</p>}
                    </div>
                )}
                {overrides.length === 0 && editing !== 'new' && <p style={muted}>No overrides: everyone follows the organisation default.</p>}
                {overrides.map(p => (
                    <div key={p.id} data-testid="policy-override" style={{ borderTop: rowBorder, padding: '10px 0' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <strong style={{ color: 'var(--text-main)' }}>{scopeName(p)}</strong>
                            <span style={{ ...muted, flex: 1 }}>{describe(p.rules)}</span>
                            {editing !== p.id && <button style={ghost} onClick={() => setEditing(p.id)}>Edit</button>}
                            <button style={ghost} aria-label={`Remove override ${scopeName(p)}`} onClick={async () => {
                                if (!window.confirm(`Remove the override for ${scopeName(p)}?`)) return;
                                try { await api.delete(`/lifecycle/settings/policies/${p.id}`); say(true, 'Override removed'); load(); } catch (e) { say(false, errorText(e, 'Remove failed')); }
                            }}><Trash2 size={13} /></button>
                        </div>
                        {editing === p.id && (
                            <div style={{ marginTop: 12 }}>
                                <OverrideEditor scope={{ departmentId: p.departmentId || '', employmentType: p.employmentType || '' }} saving={saving} onCancel={() => setEditing(null)}
                                    onSave={(rules) => save({ departmentId: p.departmentId, employmentType: p.employmentType, rules }, 'Override saved')} />
                            </div>
                        )}
                    </div>
                ))}
            </section>
        </div>
    );
};

const StepEditor = ({ step, n, users, allUsers, onChange, types }) => {
    const missing = step.type === 'USER' && step.userId && !users.some(u => u.id === step.userId);
    const missingName = missing ? (allUsers.find(u => u.id === step.userId)?.name || 'unknown person') : null;
    return (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
            <select aria-label={`Step ${n} approver type`} style={field} value={step.type} onChange={e => onChange({ type: e.target.value, ...(e.target.value === 'MANAGER' ? { level: 1 } : e.target.value === 'ROLE' ? { role: 'HR' } : { userId: '' }), skipFor: step.skipFor })}>
                <option value="MANAGER">Reporting manager</option><option value="ROLE">Anyone with a role</option><option value="USER">A specific person</option>
            </select>
            {step.type === 'MANAGER' && (
                <select aria-label={`Step ${n} manager level`} style={field} value={step.level} onChange={e => onChange({ ...step, level: Number(e.target.value) })}>
                    {[1, 2, 3, 4, 5].map(l => <option key={l} value={l}>{l === 1 ? 'Direct manager' : `${l} levels up`}</option>)}
                </select>
            )}
            {step.type === 'ROLE' && (
                <select aria-label={`Step ${n} role`} style={field} value={step.role} onChange={e => onChange({ ...step, role: e.target.value })}>
                    {Object.entries(ROLE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
            )}
            {step.type === 'USER' && (
                <select aria-label={`Step ${n} person`} style={{ ...field, borderColor: missing ? '#d97706' : undefined }} value={step.userId} onChange={e => onChange({ ...step, userId: e.target.value })}>
                    <option value="">Choose…</option>
                    {missing && <option value={step.userId}>{missingName} (no longer active: choose someone else)</option>}
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
            )}
            <span style={muted}>Skip for:</span>
            {types.map(t => (
                <label key={t} style={muted}><input type="checkbox" aria-label={`Step ${n}: skip for ${TYPE_LABELS[t]}`} checked={(step.skipFor || []).includes(t)}
                    onChange={e => onChange({ ...step, skipFor: e.target.checked ? [...(step.skipFor || []), t] : (step.skipFor || []).filter(x => x !== t) })} /> {TYPE_LABELS[t]}</label>
            ))}
        </div>
    );
};

const FlowsTab = ({ users, allUsers, say }) => {
    const [data, setData] = useState(null);
    const [drafts, setDrafts] = useState({});
    useEffect(() => {
        api.get('/lifecycle/settings/flows').then(r => { setData(r.data); setDrafts(Object.fromEntries(r.data.flows.map(f => [f.process, f.steps]))); })
            .catch(e => say(false, errorText(e, 'Failed to load approval flows')));
    }, [say]);
    if (!data) return <p style={muted}>Loading…</p>;
    const types = ['FULL_TIME', 'CONTRACTOR', 'PARTNER', 'INTERN'];
    const update = (p, steps) => setDrafts(d => ({ ...d, [p]: steps }));
    const move = (p, i, by) => { const s = [...drafts[p]]; const [x] = s.splice(i, 1); s.splice(i + by, 0, x); update(p, s); };
    // Replace only the flow that was saved or reset; other unsaved edits stay.
    const applySaved = (flow) => {
        setData(d => ({ ...d, flows: d.flows.map(f => (f.process === flow.process ? flow : f)) }));
        update(flow.process, flow.steps);
    };

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            {data.flows.map(f => (
                <section key={f.process} className="card" style={card} data-testid={`flow-${f.process}`}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <h3 style={{ margin: 0, flex: 1 }}>{f.label}</h3>
                        <span style={muted}>{f.isDefault ? 'Built-in flow' : `Customised ${new Date(f.updatedAt).toLocaleDateString()}`}</span>
                    </div>
                    <p style={{ ...muted, marginTop: 0 }}>Approvals happen in this order. A step with nobody to approve is skipped; if every step is skipped, admins (or else HR) approve. Nobody approves their own case.</p>
                    {(drafts[f.process] || []).map((s, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', borderTop: rowBorder, flexWrap: 'wrap' }}>
                            <strong style={{ width: 24, color: 'var(--text-main)' }}>{i + 1}.</strong>
                            <StepEditor step={s} n={i + 1} users={users} allUsers={allUsers} types={types} onChange={(ns) => update(f.process, drafts[f.process].map((x, j) => (j === i ? ns : x)))} />
                            <button style={ghost} aria-label={`Move step ${i + 1} up`} disabled={i === 0} onClick={() => move(f.process, i, -1)}><ArrowUp size={13} /></button>
                            <button style={ghost} aria-label={`Move step ${i + 1} down`} disabled={i === drafts[f.process].length - 1} onClick={() => move(f.process, i, 1)}><ArrowDown size={13} /></button>
                            <button style={ghost} aria-label={`Remove step ${i + 1}`} disabled={drafts[f.process].length === 1} onClick={() => update(f.process, drafts[f.process].filter((_, j) => j !== i))}><Trash2 size={13} /></button>
                        </div>
                    ))}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        <button style={ghost} disabled={(drafts[f.process] || []).length >= 6} onClick={() => update(f.process, [...drafts[f.process], { type: 'ROLE', role: 'HR' }])}><Plus size={13} /> Add step</button>
                        <span style={{ flex: 1 }} />
                        {!f.isDefault && <button style={ghost} onClick={async () => {
                            try { applySaved((await api.delete(`/lifecycle/settings/flows/${f.process}`)).data); say(true, `${f.label}: back to the built-in flow`); } catch (e) { say(false, errorText(e, 'Reset failed')); }
                        }}><RotateCcw size={13} /> Use built-in flow</button>}
                        <button className="btn btn-primary" style={primary} onClick={async () => {
                            try { applySaved((await api.put(`/lifecycle/settings/flows/${f.process}`, { steps: drafts[f.process] })).data); say(true, `${f.label}: flow saved`); } catch (e) { say(false, errorText(e, 'Save failed')); }
                        }}><Save size={14} /> Save flow</button>
                    </div>
                </section>
            ))}
        </div>
    );
};

const RULE_ROW_LABELS = { gratuity: 'Gratuity', noticeDays: 'Notice period (days)', noticeDaysOnProbation: 'Notice during probation (days)', probationMonths: 'Probation (months)', buyoutAllowed: 'Buyout allowed', encashment: 'Leave encashment', shortfallRecovery: 'Unserved notice recovery', settlementDueDays: 'Full & final due within (days)', inviteExpiryDays: 'Invite expiry (days)' };

const PreviewTab = ({ users, say }) => {
    const [userId, setUserId] = useState('');
    const [data, setData] = useState(null);
    useEffect(() => {
        let live = true; // ignore a slower answer for a previously chosen person
        if (!userId) { setData(null); return undefined; }
        api.get(`/lifecycle/settings/preview?userId=${userId}`).then(r => { if (live) setData(r.data); }).catch(e => { if (live) say(false, errorText(e, 'Preview failed')); });
        return () => { live = false; };
    }, [userId, say]);
    const fmt = (k, v) => {
        if (k === 'encashment') return v.enabled ? `${v.leaveTypes.join(', ').toLowerCase()} leave, up to ${v.maxDays} days, ${v.basis.toLowerCase()} ÷ ${v.divisor === 'ACTUAL' ? 'days in month' : v.divisor}` : 'Off';
        if (k === 'shortfallRecovery') return v.enabled ? `${v.basis.toLowerCase()} ÷ ${v.divisor === 'ACTUAL' ? 'days in month' : v.divisor}` : 'Off';
        if (k === 'gratuity') return v.enabled ? `after ${v.minYears} years, ${v.daysPerYear} days of Basic per year ÷ ${v.divisor === 'ACTUAL' ? 'days in month' : v.divisor}${v.maxAmount ? `, up to ${v.maxAmount.toLocaleString()}` : ''}` : 'Off';
        if (typeof v === 'boolean') return v ? 'Yes' : 'No';
        return String(v);
    };
    return (
        <section className="card" style={card}>
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>See what applies to</span>
                <select aria-label="Employee to check" style={field} value={userId} onChange={e => setUserId(e.target.value)}>
                    <option value="">Choose an employee…</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
            </label>
            {data && (
                <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, color: 'var(--text-main)' }}>
                        <tbody>
                            {Object.entries(data.rules).map(([k, v]) => (
                                <tr key={k} style={{ borderTop: rowBorder }}>
                                    <td style={{ padding: '6px 8px' }}>{RULE_ROW_LABELS[k] || k}</td>
                                    <td style={{ padding: '6px 8px' }} data-testid={`rule-${k}`}><strong>{fmt(k, v)}</strong></td>
                                    <td style={{ padding: '6px 8px', ...muted }}>from {data.sources[k]}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.approvals.map(a => (
                        <div key={a.process}>
                            <strong style={{ color: 'var(--text-main)' }}>{a.label}</strong>
                            <ol style={{ margin: '6px 0' }}>
                                {a.steps.map(s => (
                                    <li key={s.index} style={{ color: s.skipped ? 'var(--text-muted)' : 'var(--text-main)' }}>
                                        {s.fallback ? `${ROLE_LABELS[s.step.role]} (no other approver)` : s.step.type === 'MANAGER' ? (s.step.level === 1 ? 'Direct manager' : `Manager ${s.step.level} levels up`) : s.step.type === 'ROLE' ? ROLE_LABELS[s.step.role] : 'Named person'}
                                        {': '}
                                        {s.skipped ? <em>skipped ({s.reason})</em> : s.approvers.map(x => x.name).join(', ')}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};

const TABS = [['rules', 'Rules'], ['flows', 'Approval flows'], ['checklists', 'Checklists'], ['forms', 'Forms'], ['letters', 'Letters'], ['preview', 'Check an employee']];

const LifecycleSetup = () => {
    const [tab, setTab] = useTabParam(TABS.map(([k]) => k), 'rules');
    const [message, setMessage] = useState(null);
    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const say = useCallback((ok, text) => setMessage({ ok, text }), []);

    useEffect(() => {
        api.get('/admin/users').then(r => { setAllUsers(r.data); setUsers(r.data.filter(u => u.isActive !== false)); }).catch(() => {});
        api.get('/departments').then(r => setDepartments(r.data)).catch(() => {});
    }, []);

    return (
        <div className="container" style={{ padding: '2rem 1rem', maxWidth: 1100 }}>
            <PageHeader crumbs={crumbsFor('lifecycleSetup')} />
            <h1 style={{ marginBottom: 4 }}>Lifecycle setup</h1>
            <p style={{ ...muted, fontSize: 15, marginTop: 0 }}>The rules, approvals, checklists, forms and letters that onboarding and exits use in your organisation.</p>
            <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: rowBorder, margin: '16px 0' }}>
                {TABS.map(([k, l]) => (
                    <button key={k} role="tab" id={`tab-${k}`} aria-controls="lifecycle-panel" aria-selected={tab === k} onClick={() => { setTab(k); setMessage(null); }}
                        style={{ padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', color: tab === k ? 'var(--primary)' : 'var(--text-muted)', borderBottom: tab === k ? '2px solid var(--primary)' : '2px solid transparent', fontWeight: tab === k ? 600 : 400, marginBottom: -1 }}>{l}</button>
                ))}
            </div>
            <Message message={message} />
            <div role="tabpanel" id="lifecycle-panel" aria-labelledby={`tab-${tab}`}>
            {tab === 'rules' && <RulesTab departments={departments} say={say} />}
            {tab === 'flows' && <FlowsTab users={users} allUsers={allUsers} say={say} />}
            {tab === 'checklists' && <ChecklistsTab users={users} departments={departments} say={say} />}
            {tab === 'forms' && <FormsTab say={say} />}
            {tab === 'letters' && <LettersTab users={users} say={say} />}
            {tab === 'preview' && <PreviewTab users={users} say={say} />}
            </div>
        </div>
    );
};

export default LifecycleSetup;
